import React, { useMemo } from 'react'

import { Badge, ButtonBase, useTheme } from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import PersonIcon from '@material-ui/icons/Person'
import HomeIcon from '@material-ui/icons/Home'
import FolderIcon from '@material-ui/icons/Folder'

import { useRootInfrastructure, useAptConfig } from '../../hooks'
import { UnitRes, FolderRes } from '../../store'
import { Area, AreaConfig } from '../../models'
import { typographyStyle } from '../../theme/typographyStyle'

interface UnitSidebarProps {
    selectedAreaId: number | null
    setSelectedAreaId: (areaId: number) => void
    searchQuery: string
    onOpenUnitNotes: (unit: {
        id: number
        name: string
        channel_id: number
        unread_unit_notes: number
    }) => void
}

interface OccupiableArea {
    id: number
    label: string
    unitId: number
    unitName: string
    areaConfigId: number
}

interface UnitWithOccupiableAreas {
    id: number
    name: string
    channel_id: number
    unit_notes_msg_count: number
    unread_unit_notes: number
    folderPath: string
    occupiableAreas: OccupiableArea[]
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        sidebar: {
            width: 240,
            minWidth: 240,
            maxWidth: 240,
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
        },
        header: {
            padding: theme.spacing(2.5, 3),
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}03 100%)`,
            flexShrink: 0,
        },
        headerTop: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            marginBottom: theme.spacing(2),
        },
        headerIcon: {
            width: 36,
            height: 36,
            borderRadius: theme.spacing(1),
            backgroundColor: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        headerTitle: {
            ...(typographyStyle(theme.typography.subtitle1) as Record<
                string,
                unknown
            >),
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
        statsContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
        },
        statsRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        statsLabel: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
        },
        statsValue: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            fontWeight: 600,
            color: theme.palette.primary.main,
        },
        progressBar: {
            height: 6,
            borderRadius: 3,
            backgroundColor: theme.palette.grey[200],
            '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                backgroundColor: theme.palette.primary.main,
            },
        },
        scrollContainer: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: theme.spacing(2, 1.5, 2, 2),
            '&::-webkit-scrollbar': {
                width: 6,
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[300],
                borderRadius: 3,
                '&:hover': {
                    backgroundColor: theme.palette.grey[400],
                },
            },
        },
        unitGroup: {
            marginBottom: theme.spacing(2),
            overflow: 'visible',
        },
        unitLabel: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            fontWeight: 600,
            color: theme.palette.text.primary,
            padding: theme.spacing(1.5, 2, 0.75, 1.5),
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            overflow: 'visible',
        },
        unitLabelIcon: {
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.primary.main,
            flexShrink: 0,
        },
        unitLabelText: {
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.75),
            overflow: 'visible',
        },
        folderPathText: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            fontWeight: 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flexShrink: 1,
        },
        unitNameText: {
            fontSize: 13,
            fontWeight: 600,
            color: theme.palette.text.primary,
            cursor: 'pointer',
            borderRadius: theme.spacing(0.5),
            padding: theme.spacing(0.25, 0.5),
            maxWidth: 110,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block',
            lineHeight: 1.4,
            transition: 'background-color 0.15s ease, color 0.15s ease',
            '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: `${theme.palette.primary.main}12`,
            },
        },
        areaButton: {
            width: 'calc(100% - 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme.spacing(1.25, 1.5),
            borderRadius: theme.spacing(1),
            marginBottom: theme.spacing(0.5),
            marginLeft: theme.spacing(1.5),
            textAlign: 'left',
            transition: 'all 0.2s ease',
            border: `1px solid transparent`,
            '&:hover': {
                backgroundColor: theme.palette.grey[50],
                borderColor: theme.palette.grey[200],
                transform: 'translateX(2px)',
            },
        },
        areaButtonSelected: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                borderColor: 'transparent',
                transform: 'translateX(2px)',
            },
        },
        areaInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        areaIcon: {
            width: 28,
            height: 28,
            borderRadius: theme.spacing(0.75),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.grey[100],
        },
        areaIconSelected: {
            backgroundColor: 'rgba(255,255,255,0.2)',
        },
        areaName: {
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
            fontWeight: 600,
        },
        emptyState: {
            padding: theme.spacing(4),
            textAlign: 'center',
            color: theme.palette.text.secondary,
        },
    }),
)

export const UnitSideBar = ({
    selectedAreaId,
    setSelectedAreaId,
    searchQuery,
    onOpenUnitNotes,
}: UnitSidebarProps) => {
    const theme = useTheme()
    const classes = useStyles()

    const { unitList, folderList } = useRootInfrastructure(true)
    const { areaConfigList } = useAptConfig({ areaConfigList: true })

    // Create a map of area config id to occupiable status
    const areaConfigMap = useMemo(() => {
        const map: Record<number, AreaConfig> = {}
        areaConfigList?.forEach((config) => {
            map[config.id] = config
        })
        return map
    }, [areaConfigList])

    // Create a map of folder id to folder
    const folderMap = useMemo(() => {
        const map: Record<number, FolderRes> = {}
        folderList?.forEach((folder) => {
            map[folder.id] = folder
        })
        return map
    }, [folderList])

    // Transform units to only show occupiable areas
    const unitsWithOccupiableAreas = useMemo((): UnitWithOccupiableAreas[] => {
        if (!unitList) return []

        return unitList
            .map((unit: UnitRes) => {
                const folder = folderMap[unit.folder_id]
                const folderPath = folder ? `${folder.path}${folder.name}` : ''

                const occupiableAreas = unit.areas
                    .filter((area: Area) => {
                        const areaConfig = areaConfigMap[area.area_config]
                        return areaConfig?.occupiable === true
                    })
                    .map((area: Area, index: number) => ({
                        id: area.id,
                        label: `${area.area_config_name || ''} ${
                            area.area_label
                        }`.trim(),
                        unitId: unit.id,
                        unitName: unit.name,
                        areaConfigId: area.area_config,
                    }))

                return {
                    id: unit.id,
                    name: unit.name,
                    channel_id: unit.channel_id,
                    unit_notes_msg_count: unit.unit_notes_msg_count,
                    unread_unit_notes: unit.unread_unit_notes,
                    folderPath,
                    occupiableAreas,
                }
            })
            .filter((unit) => unit.occupiableAreas.length > 0)
    }, [unitList, areaConfigMap, folderMap])

    // Calculate total occupiable areas
    const totalAreas = useMemo(() => {
        return unitsWithOccupiableAreas.reduce(
            (sum, unit) => sum + unit.occupiableAreas.length,
            0,
        )
    }, [unitsWithOccupiableAreas])

    // Filter units based on search query
    const filteredUnits = useMemo(() => {
        if (!searchQuery) return unitsWithOccupiableAreas

        const query = searchQuery.toLowerCase()
        return unitsWithOccupiableAreas.filter(
            (unit) =>
                unit.name.toLowerCase().includes(query) ||
                unit.occupiableAreas.some((area) =>
                    area.label.toLowerCase().includes(query),
                ),
        )
    }, [unitsWithOccupiableAreas, searchQuery])

    if (!unitList || unitList.length === 0) {
        return (
            <aside className={classes.sidebar}>
                <div className={classes.header}>
                    <div className={classes.headerTop}>
                        <div className={classes.headerIcon}>
                            <HomeIcon
                                style={{
                                    color: theme.palette.common.white,
                                    fontSize: 20,
                                }}
                            />
                        </div>
                        <span className={classes.headerTitle}>Bed Spaces</span>
                    </div>
                </div>
                <div className={classes.emptyState}>No units available</div>
            </aside>
        )
    }

    return (
        <aside className={classes.sidebar}>
            <div className={classes.header}>
                <div className={classes.headerTop}>
                    <div className={classes.headerIcon}>
                        <HomeIcon
                            style={{
                                color: theme.palette.common.white,
                                fontSize: 20,
                            }}
                        />
                    </div>
                    <span className={classes.headerTitle}>Bed Spaces</span>
                </div>
                <div className={classes.statsContainer}>
                    <div className={classes.statsRow}>
                        <span className={classes.statsLabel}>Total Spaces</span>
                        <span className={classes.statsValue}>{totalAreas}</span>
                    </div>
                    <div className={classes.statsRow}>
                        <span className={classes.statsLabel}>
                            {unitsWithOccupiableAreas.length} units
                        </span>
                        <span className={classes.statsLabel}>
                            {totalAreas} beds
                        </span>
                    </div>
                </div>
            </div>

            <div className={classes.scrollContainer}>
                {filteredUnits.map((unit) => (
                    <div key={unit.id} className={classes.unitGroup}>
                        <div className={classes.unitLabel}>
                            <div className={classes.unitLabelIcon}>
                                <FolderIcon style={{ fontSize: 16 }} />
                            </div>
                            <div className={classes.unitLabelText}>
                                {unit.folderPath && (
                                    <span className={classes.folderPathText}>
                                        {unit.folderPath}
                                    </span>
                                )}
                                <Badge
                                    badgeContent={unit.unit_notes_msg_count}
                                    color={
                                        (unit.unread_unit_notes ?? 0) > 0
                                            ? 'secondary'
                                            : 'primary'
                                    }
                                >
                                    <span
                                        className={classes.unitNameText}
                                        title="Open unit notes"
                                        onClick={() => {
                                            if (unit.channel_id) {
                                                onOpenUnitNotes({
                                                    id: unit.id,
                                                    name: unit.name,
                                                    channel_id: unit.channel_id,
                                                    unread_unit_notes:
                                                        unit.unread_unit_notes,
                                                })
                                            }
                                        }}
                                    >
                                        {unit.name}
                                    </span>
                                </Badge>
                            </div>
                        </div>

                        {unit.occupiableAreas.map((area) => {
                            const isSelected = selectedAreaId === area.id
                            return (
                                <ButtonBase
                                    key={area.id}
                                    onClick={() => setSelectedAreaId(area.id)}
                                    className={`${classes.areaButton} ${
                                        isSelected
                                            ? classes.areaButtonSelected
                                            : ''
                                    }`}
                                >
                                    <div className={classes.areaInfo}>
                                        <div
                                            className={`${classes.areaIcon} ${
                                                isSelected
                                                    ? classes.areaIconSelected
                                                    : ''
                                            }`}
                                        >
                                            <PersonIcon
                                                style={{
                                                    fontSize: 16,
                                                    color: isSelected
                                                        ? theme.palette.common
                                                              .white
                                                        : theme.palette.text
                                                              .secondary,
                                                }}
                                            />
                                        </div>
                                        <span className={classes.areaName}>
                                            {area.label}
                                        </span>
                                    </div>
                                </ButtonBase>
                            )
                        })}
                    </div>
                ))}
            </div>
        </aside>
    )
}
